/**
 * DESTASH STATUS BADGE
 *
 * A clickable badge that shows destash status (Available/Pending/Sold).
 * Now with quantity tracking!
 * - Shows how many are available
 * - "Sell 1" option decrements quantity
 * - Auto-marks as Sold when quantity hits 0
 */

import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ChevronDown, Loader2, Minus, Plus } from 'lucide-react';
import { AppIcons } from '../constants/icons';

interface DestashStatusBadgeProps {
  itemId: string;
  userId: string;
  currentStatus?: 'available' | 'pending' | 'sold' | string;
  isForDestash?: boolean;
  quantity?: number;  // Total quantity owned
  destashQuantityAvailable?: number;  // How many still for sale
  onStatusChange?: (newStatus: string, newQuantityAvailable?: number) => void;
}

const DestashStatusBadge: React.FC<DestashStatusBadgeProps> = ({
  itemId,
  userId,
  currentStatus = 'available',
  isForDestash = false,
  quantity = 1,
  destashQuantityAvailable,
  onStatusChange
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [status, setStatus] = useState(currentStatus);
  const [qtyAvailable, setQtyAvailable] = useState(destashQuantityAvailable ?? quantity);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Don't render if not for destash
  if (!isForDestash) return null;

  const statusOptions = [
    { value: 'available', label: 'Available', icon: AppIcons.Available, iconColor: 'text-green-500', bgColor: 'bg-green-500', hoverColor: 'hover:bg-green-600' },
    { value: 'pending', label: 'Pending', icon: AppIcons.Pending, iconColor: 'text-yellow-500', bgColor: 'bg-yellow-500', hoverColor: 'hover:bg-yellow-600' },
    { value: 'sold', label: 'Sold', icon: AppIcons.Sold, iconColor: 'text-red-500', bgColor: 'bg-red-500', hoverColor: 'hover:bg-red-600' },
  ];

  const currentStatusInfo = statusOptions.find(s => s.value === status) || statusOptions[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Update state when props change
  useEffect(() => {
    setStatus(currentStatus);
    setQtyAvailable(destashQuantityAvailable ?? quantity);
  }, [currentStatus, destashQuantityAvailable, quantity]);

  // Handle selling one item (actually sold!)
  const handleSoldOne = async () => {
    if (isChanging || qtyAvailable <= 0) return;
    
    if (!userId || !itemId) {
      console.error('Missing userId or itemId:', { userId, itemId });
      return;
    }

    setIsChanging(true);
    setShowDropdown(false);

    const newQty = qtyAvailable - 1;
    const newStatus = newQty === 0 ? 'sold' : status;

    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      await updateDoc(itemRef, {
        destashQuantityAvailable: newQty,
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      setQtyAvailable(newQty);
      setStatus(newStatus);
      onStatusChange?.(newStatus, newQty);
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Failed to update. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  // Handle setting quantity for sale (Sell All / Keep 1 buttons)
  const handleSetQuantityForSale = async (newQty: number) => {
    if (isChanging || newQty === qtyAvailable) return;
    
    if (!userId || !itemId) {
      console.error('Missing userId or itemId:', { userId, itemId });
      return;
    }

    // Clamp to valid range
    newQty = Math.max(0, Math.min(quantity, newQty));

    setIsChanging(true);

    const newStatus = newQty === 0 ? 'sold' : (status === 'sold' ? 'available' : status);

    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      await updateDoc(itemRef, {
        destashQuantityAvailable: newQty,
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      setQtyAvailable(newQty);
      setStatus(newStatus);
      onStatusChange?.(newStatus, newQty);
    } catch (err) {
      console.error('Error setting quantity:', err);
      alert('Failed to update. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  // Handle selling one item (remove from sale - the minus button)
  const handleSellOne = async () => {
    if (isChanging || qtyAvailable <= 0) return;
    
    if (!userId || !itemId) {
      console.error('Missing userId or itemId:', { userId, itemId });
      return;
    }

    setIsChanging(true);
    setShowDropdown(false);

    const newQty = qtyAvailable - 1;
    const newStatus = newQty === 0 ? 'sold' : status;

    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      await updateDoc(itemRef, {
        destashQuantityAvailable: newQty,
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      setQtyAvailable(newQty);
      setStatus(newStatus);
      onStatusChange?.(newStatus, newQty);
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Failed to update. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  // Handle adding one back (undo a sale)
  const handleAddOne = async () => {
    if (isChanging || qtyAvailable >= quantity) return;
    
    if (!userId || !itemId) {
      console.error('Missing userId or itemId:', { userId, itemId });
      return;
    }

    setIsChanging(true);
    setShowDropdown(false);

    const newQty = qtyAvailable + 1;
    const newStatus = newQty > 0 && status === 'sold' ? 'available' : status;

    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      await updateDoc(itemRef, {
        destashQuantityAvailable: newQty,
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      setQtyAvailable(newQty);
      setStatus(newStatus);
      onStatusChange?.(newStatus, newQty);
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Failed to update. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  // Handle status change (without quantity change)
  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === status || isChanging) return;
    
    if (!userId || !itemId) {
      console.error('Missing userId or itemId:', { userId, itemId });
      return;
    }

    setIsChanging(true);
    setShowDropdown(false);

    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      await updateDoc(itemRef, {
        destashStatus: newStatus,
        updatedAt: serverTimestamp(),
      });

      setStatus(newStatus);
      onStatusChange?.(newStatus, qtyAvailable);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsChanging(false);
    }
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isChanging) {
      setShowDropdown(!showDropdown);
    }
  };

  // Show quantity if more than 1 total
  const showQuantity = quantity > 1;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={isChanging}
        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-white ${currentStatusInfo.bgColor} ${currentStatusInfo.hoverColor} transition-default cursor-pointer`}
      >
        {isChanging ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <>
            {React.createElement(currentStatusInfo.icon, { className: "w-3 h-3" })}
            <span>
              {currentStatusInfo.label}
              {showQuantity && `: ${qtyAvailable}/${quantity}`}
            </span>
            <ChevronDown className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && !isChanging && (
        <div 
          className="absolute left-0 mt-1 bg-white border border-slate-600 rounded-lg shadow-xl overflow-hidden"
          style={{ zIndex: 9999, minWidth: '160px' }}
        >
          {/* Quantity Controls - Only show if quantity > 1 */}
          {showQuantity && (
            <>
              <div className="px-3 py-2 bg-slate-800/60 border-b border-slate-600">
                <p className="text-xs text-glass-muted font-bold mb-2 font-body">
                  For Sale: {qtyAvailable} of {quantity}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddOne();
                    }}
                    disabled={qtyAvailable >= quantity}
                    className="p-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-default"
                    title="Add 1 to sale"
                  >
                    <Plus className="icon-sm text-glass-secondary" />
                  </button>
                  <span className="text-lg font-bold text-glass font-body">
                    {qtyAvailable}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSellOne();
                    }}
                    disabled={qtyAvailable <= 0}
                    className="p-1 rounded bg-slate-700 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-default"
                    title="Remove 1 from sale"
                  >
                    <Minus className="icon-sm text-white" />
                  </button>
                </div>
                
                {/* Quick Set Buttons */}
                <div className="flex gap-1 mt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSetQuantityForSale(quantity); // Sell all
                    }}
                    className={`flex-1 px-2 py-1 text-xs rounded font-bold transition-default ${
                      qtyAvailable === quantity 
                        ? 'bg-slate-700 text-white' 
                        : 'bg-gray-200 text-glass-secondary hover:bg-gray-300'
                    }`}
                  >
                    Sell All
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSetQuantityForSale(quantity - 1); // Keep 1
                    }}
                    className={`flex-1 px-2 py-1 text-xs rounded font-bold transition-default ${
                      qtyAvailable === quantity - 1 
                        ? 'bg-slate-700 text-white' 
                        : 'bg-gray-200 text-glass-secondary hover:bg-gray-300'
                    }`}
                  >
                    Keep 1
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Quick Sell 1 - Only if quantity > 1 and items available */}
          {showQuantity && qtyAvailable > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSoldOne();
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 flex items-center gap-2 transition-default text-green-700 font-bold border-b border-gray-100 font-body"
            >
              <AppIcons.SoldAction className="icon-sm text-current" aria-label="Sold" />
              <span>Sold 1!</span>
            </button>
          )}

          {/* Status Options */}
          {statusOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusChange(option.value);
                }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-800/40 flex items-center gap-2 transition-default font-body ${
                  option.value === status ? 'bg-slate-800/40 font-bold' : ''
                }`}
              >
                <IconComponent className={`icon-sm fill-current ${option.iconColor}`} aria-label={option.label} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DestashStatusBadge;
